import { MessageSquare, Trash2, Plus, Clock, ChevronRight } from 'lucide-react';
import type { Conversation } from '../../types/chat';

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onDelete,
  onNew
}: ConversationListProps) {
  // Trier par date (plus récent en premier)
  const sortedConversations = [...conversations].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days} jours`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="border-r border-border w-72 flex flex-col bg-card">
      {/* Header avec bouton nouvelle conversation */}
      <div className="p-4 border-b border-border">
        <button
          onClick={onNew}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          <span className="font-medium">Nouvelle conversation</span>
        </button>
      </div>
      
      {/* Liste des conversations */}
      <div className="flex-1 overflow-y-auto">
        {sortedConversations.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Aucune conversation</p>
            <p className="text-xs mt-1">Commencez par poser une question à ADHA</p>
          </div>
        ) : (
          <div className="py-2">
            {sortedConversations.map(conversation => (
              <div
                key={conversation.id}
                className={`
                  group relative mx-2 my-1 px-3 py-3 rounded-xl cursor-pointer transition-all
                  ${activeId === conversation.id 
                    ? 'bg-primary/10 border border-primary/20' 
                    : 'hover:bg-muted border border-transparent'
                  }
                `}
                onClick={() => onSelect(conversation.id)}
              >
                <div className="flex items-start space-x-3">
                  {/* Icône */}
                  <div className={`
                    w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                    ${activeId === conversation.id 
                      ? 'bg-primary/20 text-primary' 
                      : 'bg-muted text-muted-foreground'
                    }
                  `}>
                    <MessageSquare className="h-4 w-4" />
                  </div>
                  
                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <p className={`
                      text-sm font-medium truncate
                      ${activeId === conversation.id ? 'text-primary' : 'text-foreground'}
                    `}>
                      {conversation.title || 'Nouvelle conversation'}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {formatDate(conversation.timestamp)}
                      </span>
                      {conversation.messages?.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          • {conversation.messages.length} msg
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Indicateur actif */}
                  {activeId === conversation.id && (
                    <ChevronRight className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </div>
                
                {/* Bouton supprimer (visible au hover) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conversation.id);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500 transition-all"
                  title="Supprimer"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer avec statistiques */}
      {conversations.length > 0 && (
        <div className="p-3 border-t border-border text-center">
          <span className="text-xs text-muted-foreground">
            {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}